import { IterableMap } from "@redotech/react-util/component";
import { useInput } from "@redotech/react-util/form";
import LayoutAltIcon from "@redotech/redo-icons/layout-alt-01.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { StorefrontFeatures } from "@redotech/redo-model/split";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { groupInput, input, listInput } from "@redotech/ui/form";
import { memo } from "react";

type TextEdit = StorefrontFeatures["textEdits"][number];

const textEditInput = groupInput({
  selector: input<string>(),
  textToReplace: input<string>(),
  replacementText: input<string>(),
});

const textEditsForm = listInput(
  () => textEditInput,
  (): TextEdit => ({ selector: "", textToReplace: "", replacementText: "" }),
  (_, index) => index,
);

interface TextEditsConfigModalProps {
  treatmentName: string;
  textEdits: StorefrontFeatures["textEdits"];
  onSave: (textEdits: StorefrontFeatures["textEdits"]) => void;
  onClose: () => void;
}

export const TextEditsConfigModal = memo(function TextEditsConfigModal({
  treatmentName,
  textEdits,
  onSave,
  onClose,
}: TextEditsConfigModalProps) {
  const input = useInput(textEditsForm, textEdits);

  return (
    <RedoModal
      footerBorder
      headerIconAlignment="inline"
      onModalCloseRequested={onClose}
      primaryButton={{ text: "Save", onClick: () => onSave(input.value) }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle="Replace text on banners and other elements on your storefront"
      title={`${treatmentName} - Content edits`}
      TitleIcon={LayoutAltIcon}
    >
      <Flex dir="column" gap="2xl" w="full">
        <IterableMap items={input.inputs} keyFn={(_, i) => i}>
          {(textEditInput, i) => (
            <Flex dir="column" gap="md" w="full">
              <Flex align="center" dir="row" gap="md" w="full">
                <Flex flex={1}>
                  <RedoTextInput
                    description="CSS selector for the element containing the text to edit"
                    fullWidth
                    label="Selector"
                    setValue={textEditInput.inputs.selector.setValue}
                    size="sm"
                    value={textEditInput.inputs.selector.value}
                  />
                </Flex>
                <RedoButton
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={() =>
                    input.setValue(
                      input.value.filter((_, index) => index !== i),
                    )
                  }
                  size="sm"
                  theme="destructive"
                />
              </Flex>
              <Flex dir="row" gap="md" w="full">
                <RedoTextInput
                  fullWidth
                  label="Text to replace"
                  setValue={textEditInput.inputs.textToReplace.setValue}
                  size="sm"
                  value={textEditInput.inputs.textToReplace.value}
                />
                <RedoTextInput
                  fullWidth
                  label="Replacement text"
                  setValue={textEditInput.inputs.replacementText.setValue}
                  size="sm"
                  value={textEditInput.inputs.replacementText.value}
                />
              </Flex>
            </Flex>
          )}
        </IterableMap>
        <RedoButton
          hierarchy="secondary"
          IconLeading={PlusIcon}
          onClick={() =>
            input.setValue([
              ...input.value,
              { selector: "", textToReplace: "", replacementText: "" },
            ])
          }
          size="sm"
          text="Add text edit"
        />
      </Flex>
    </RedoModal>
  );
});
